import { useCallback, useEffect, useState } from "react";
import { getCsrfHeader } from "../lib/csrf";

// IP-221: a "platform credential" is a provider account WE own (e.g. our
// ServiceM8 account), used to enumerate that provider's catalog tools -
// separate from any customer's connection. This hook reads whether one is
// connected and starts the admin OAuth flow to (re)connect it.
export type PlatformCredentialStatus = "active" | "error" | "absent";

export type UseAdminPlatformCredentialResult = {
  status: PlatformCredentialStatus | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  // POSTs to start the OAuth flow and returns the authorize URL for the caller
  // to navigate to (a full-page redirect; ServiceM8 OAuth can't run in a popup).
  startConnect: () => Promise<string>;
};

const base = (provider: string): string =>
  `/api/admin/integration-catalog/${encodeURIComponent(provider)}`;

export const useAdminPlatformCredential = (
  provider: string,
): UseAdminPlatformCredentialResult => {
  const [status, setStatus] = useState<PlatformCredentialStatus | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${base(provider)}/platform-status`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`status fetch failed (${res.status})`);
      const body = (await res.json()) as { status: PlatformCredentialStatus };
      setStatus(body.status);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [provider]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const startConnect = useCallback(async (): Promise<string> => {
    const res = await fetch(`${base(provider)}/platform-connect`, {
      method: "POST",
      credentials: "include",
      headers: { ...getCsrfHeader() },
    });
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ?? `connect failed (${res.status})`);
    }
    const body = (await res.json()) as { redirectUrl: string };
    return body.redirectUrl;
  }, [provider]);

  return { status, loading, error, refresh, startConnect };
};
